/**
 * Google Ad Manager Configuration for Squares
 * Ad unit paths and sizes based on DraftKings Tagging Architecture
 * Sticky footer: 320x50 (mobile), 728x90 or 970x90 (display/tablet) per squares_requirements.md
 */

import type {
  GoogleAdsPlacement,
  GoogleAdsPlacementKey,
  GoogleAdsPlacements,
  TenantConfig,
} from '@/hooks/useTenant';

export interface GAMConfig {
  networkId: string;
  enabled: boolean;
}

export const getGAMConfigFromTenant = (tenant: TenantConfig | undefined): GAMConfig | null => {
  if (!tenant || !tenant.gamEnabled) {
    return null;
  }
  return {
    networkId: tenant.gamNetworkId || '',
    enabled: tenant.gamEnabled,
  };
};

export interface ResponsiveSizeMapping {
  viewport: [number, number];
  sizes: [number, number][] | [];
}

/** `SQUARES_POST_ENTRY` -> `squares-post-entry`: every underscore, not just the first. */
const keyToSlug = (placementKey: string | undefined): string =>
  placementKey ? placementKey.toLowerCase().replace(/_/g, '-') : '';

export const deriveAdUnitPath = (basePath: string, placement: GoogleAdsPlacement): string => {
  const segment =
    placement.targeting?.s3 || keyToSlug(placement.placementKey) || 'sticky-banner-footer';
  return `${basePath}/${segment}`;
};

export const deriveDivIdPrefix = (placement: GoogleAdsPlacement): string => {
  return placement.targeting?.s3 || keyToSlug(placement.placementKey) || 'body';
};

/**
 * Div id prefixes used when a placement carries no `s3` targeting to derive one from.
 * Keys absent here fall back to 'ad'.
 */
const DEFAULT_DIV_ID_PREFIX: Partial<Record<GoogleAdsPlacementKey, string>> = {
  BODY: 'body',
  STICKY_FOOTER: 'sticky-banner-footer',
  SQUARES_POST_ENTRY: 'squares-post-entry-ad',
};

export const generateAdDivId = (
  placements: GoogleAdsPlacements,
  placement: GoogleAdsPlacementKey,
  isMultipleForPosition: boolean = false,
  index: number = 0
): string => {
  const prefix = placements[placement]?.divIdPrefix || DEFAULT_DIV_ID_PREFIX[placement] || 'ad';
  return isMultipleForPosition && index > 0 ? `${prefix}-${index}` : prefix;
};

/**
 * SAVED FOR LATER - Option A/B override logic (see stash "GAM Squares integration + admin override"):
 * - Option A (GAM-based): derived path {basePath}/sticky-footer + pid=contestId
 * - Option B (Admin override): explicit ad unit path from ad_units Legacy, contest_list_placement, or sticky_footer_placement.adUnitPath
 * Use resolveContestAdPlacement(contest, contestId, basePath) for per-contest in-card ads when uncommenting.
 */

/**
 * Slot-level targeting layered on top of the admin-configured targeting for any
 * contest-scoped placement — post-entry, over-logo and sticky-footer all route through
 * here. `pid` and `s5` both scope the request to a single contest in GAM — `s5` is the
 * contest-id slot in the DraftKings tagging architecture, `pid` is the line-item
 * targeting key — and both are per-request data admin can't hold.
 *
 * The derived contest id wins over any configured `s5`: a value saved in Admin would
 * be a stale, contest-agnostic default.
 */
export const withContestTargeting = (
  configured: Record<string, string> | null | undefined,
  contestId?: string | null
): Record<string, string> => {
  const targeting: Record<string, string> = { ...(configured ?? {}) };
  const trimmed = contestId?.trim();
  if (trimmed) {
    targeting.pid = trimmed;
    targeting.s5 = trimmed;
  }
  return targeting;
};

/**
 * Build GAM placement for contest list views (Details tab, My Games tab).
 * Uses path /{networkId}/{s1}/{s2}/contest-list, 320x50 size.
 *
 * @param networkId - GAM network ID
 * @param existingTargeting - Optional existing targeting object for s1 and s2 values.
 */
export function buildContestListPlacement(
  networkId: string,
  existingTargeting?: Record<string, string> | null
): Omit<GoogleAdsPlacement, 'placementKey'> & { placementKey: GoogleAdsPlacementKey } {
  const s1 = existingTargeting?.s1 || 'dfs';
  const s2 = existingTargeting?.s2 || 'free-to-play';
  const targeting: Record<string, string> = {
    s1,
    s2,
    s3: 'contest-list',
    pos: 'contest-list',
  };

  return {
    placementKey: 'STICKY_FOOTER',
    enabled: true,
    adUnitPath: `/${networkId}/${s1}/${s2}/contest-list`,
    sizes: [[320, 50]],
    targeting,
  };
}

/**
 * Build GAM placement for Rules view (contest rules/how-to-play page).
 * Uses path /{networkId}/{s1}/{s2}/rules, 320x50 size.
 * Sends pid and s5 = contestId for contest-specific targeting in GAM.
 *
 * @param networkId - GAM network ID
 * @param contestId - Optional contest ID for pid/s5 targeting
 * @param existingTargeting - Optional existing targeting object for s1 and s2 values.
 */
export function buildRulesPlacement(
  networkId: string,
  contestId?: string | null,
  existingTargeting?: Record<string, string> | null
): Omit<GoogleAdsPlacement, 'placementKey'> & { placementKey: GoogleAdsPlacementKey } {
  const s1 = existingTargeting?.s1 || 'free-to-play';
  const s2 = existingTargeting?.s2 || 'squares';
  // Routed through the shared helper so `pid` and `s5` can never drift apart.
  const targeting = withContestTargeting({ s1, s2, s3: 'rules', pos: 'rules' }, contestId);

  return {
    placementKey: 'STICKY_FOOTER',
    enabled: true,
    adUnitPath: `/${networkId}/${s1}/${s2}/rules`,
    sizes: [[320, 50]],
    targeting,
  };
}

/**
 * Fallback sizes per placement, used only when ad-ops has saved a placement with no
 * sizes of its own. `AdSlot` otherwise defaults to the footer-shaped [[320,50],[728,90]],
 * which is the wrong shape for a modal banner.
 *
 * 300x250 (MPU) mirrors the Pick'em POST_ENTRY placement, which is trafficked at that
 * size. The larger display sizes from squares_requirements.md (728x500 / 970x500) are
 * deliberately not defaulted here: `MainLayout` caps the Squares column at
 * `md:max-w-[500px]` so they cannot fit, and `AdSlot` applies a single
 * `Math.max(...heights)` minHeight at every breakpoint, which would reserve a 500px-tall
 * empty block inside the modal. Ad-ops can still set them explicitly in Admin once the
 * layout supports them (DKPLAT-679).
 */
export const DEFAULT_PLACEMENT_SIZES: Partial<Record<GoogleAdsPlacementKey, [number, number][]>> = {
  SQUARES_POST_ENTRY: [[300, 250]],
};
